import React, {useState, useEffect} from 'react';
import {Tabs, Tab, Grid, Paper, Button} from '@material-ui/core';
import {Prompt} from "react-router-dom";
import {useStateWithCallbackLazy} from "use-state-with-callback";
import useStepper from "../../shared/FormTabs/hooks/useStepper";
import FormTabs from "../../shared/FormTabs/FormTabs";
import FormFooter from "../../shared/FormTabs/FormFooter";
import StepperFormTabs from "../../shared/FormTabs/StepperFormTabs";
import CustomSnackbar from "../../../user/shared/CustomSnackbar";
import ChangeLogModal from "../../shared/ChangeLogModal";
import TabPanel from "../../shared/FormTabs/TabPanel";
import InvestorService from "../../services/InvestorService";
import InvestorInfoSection from "./InvestorInfoSection";
import InvestorDetailsSideBar from "./InvestorDetailsSideBar";
import NotesDialog from "../../shared/Dialogs/NotesDialog";
import AdminService from "../../services/AdminService";
import useAdminNotes from "../../../../hooks/useAdminNotes";
import useAsyncRequest from "../../../../hooks/useAsyncRequest";
import RegenerateBio from "../../shared/Buttons/RegenerateBio";
import PrivateWealthTeamsService from "../../services/PrivateWealthTeamsService";
import {isDateSimilar} from "../../helpers/dateHelper";

const InvestorForm = ({
                              adminUserSession,
                              investorId,
                              onClose,
                              open,
                              afterCreate,
                              afterUpdate,
                              itemIndex,
                              snackbar,
                              logInAs,
                              isDeleted,
                              investorExtraData,
                              dataType,
                              ...props
                          }) => {
    const [investor, setInvestor] = useState(null);
    const [validationErrors, setValidationErrors] = useState('')

    const [openChanges, setOpenChanges] = useState(false);
    const [changes, setChanges] = useState(null)
    const [avatar, setAvatar] = useState(null);
    const [avatarFile, setAvatarFile] = useState(null);
    const [fileUploadingSnackBar, setFileUploadingSnackBar] = useState({showSnackBar: false});

    const [clickOnArrowBack, setClickOnArrowBack] = useStateWithCallbackLazy(false);
    const [userMadeChanges, setUserMadeChanges] = useStateWithCallbackLazy(false);
    const [saving, setSaving] = useState(false);
    const adminNotes = useAdminNotes(investorId, "Investor", snackbar, InvestorService.notes);

    useEffect(() => {
        if (investorId !== undefined) {
            InvestorService.edit(investorId, adminUserSession)
                .then((response) => {
                    const {investor, status: responseStatus} = response.data;

                    setInvestor(null);
                    setInvestor(prevState => ({
                        ...prevState,
                        ...investor,
                    }));
                    setAvatar(investor?.photo?.url);
                    AdminService.registerVisit(investorId, "Investor", adminUserSession);
                })
        } else {
            InvestorService.new(adminUserSession)
                .then((response) => {
                    const {investor, status: responseStatus} = response.data;

                    setInvestor(prevState => ({
                        ...prevState,
                        ...investor,
                    }));
                })
        }
    }, [investorId])

    const handleCloseSnackBar = () => {
        setValidationErrors('')
    }

    const handleCreate = (investor) => {
        InvestorService.create(adminUserSession, investor).then((response) => {
            const {
                data: {
                    status,
                    message,
                    investor: savedInvestor,
                }
            } = response;

            if (status === 'success') {
                if (avatarFile) {
                    setFileUploadingSnackBar({
                        showSnackBar: true,
                        snackBarMessage: 'The photo is uploading please wait',
                        stayOpen: true,
                    })
                    InvestorService.uploadAvatar(savedInvestor.id, adminUserSession, avatarFile).then(({data}) => {
                        if (data?.photo?.url) {
                            setUserMadeChanges(false, () => {
                                afterCreate();
                            })
                        } else {
                            setFileUploadingSnackBar({
                                showSnackBar: true,
                                snackBarMessage: 'This photo is not supported please try another one',
                                stayOpen: true,
                            })
                            setTimeout(() => {
                                setUserMadeChanges(false, () => {
                                    afterCreate();
                                })
                            }, 3500);
                        }
                    });
                } else {
                    setUserMadeChanges(false, () => {
                        afterCreate();
                    })
                }

            } else {
                snackbar.show(message, "warning");
                console.log('message', message)
            }
        }).catch((thrown) => {
            snackbar.show("There was an error when creating Investor", "warning");
        }).finally(() => {
            setSaving(false)
        });
    }

    const handleUpdate = (investor) => {
        InvestorService.update(investorId, adminUserSession, investor)
            .then(({data}) => {
                const {
                    status: statusUpdate,
                    message,
                    investor: savedInvestor,
                } = data;

                if (statusUpdate === 'success') {
                    setUserMadeChanges(false, () => onClose());
                    afterUpdate(savedInvestor, itemIndex);
                } else {
                    snackbar.show(message);
                }
            }).catch((thrown) => {
                snackbar.show("There was an error when updating Investor", "warning");
            }).finally(() => {
                setSaving(false)
            });
    }

    const handleOnChange = (event) => {
        event.persist();
        if (!userMadeChanges) {
            setUserMadeChanges(true)
        }
        const {
            name,
            value,
            type,
            checked
        } = event.target;

        const valueSelected = {[name]: type === 'checkbox' ? checked : value}

        setInvestor(prevState => ({
            ...prevState,
            ...valueSelected
        }));
    }

    const handleOnChangeInField = (field, value, byUser = true, contactsChange = false) => {
        if (!userMadeChanges && byUser && !contactsChange) {
            setUserMadeChanges(true)
        }
        setInvestor(prevState => ({
            ...prevState, [field]: value
        }));
    }

    const validationsErrors = () => {
        return '';
    }

    const handleSubmit = (event) => {
        event.persist();
        event.preventDefault();
        const errors = validationsErrors();
        setSaving(true)
        if (errors) {
            setValidationErrors(errors)
        } else {
            if (investorId !== undefined) {
                handleUpdate(investor);
            } else {
                handleCreate(investor);
            }
        }
    }

    const handleChangeFileInput = (files) => {
        const file = files[0];
        const reader = new FileReader();
        reader.onload = function (event) {
            setAvatar(event.target.result);
        };
        reader.readAsDataURL(file);
        setAvatarFile(file);
        if (!!investorId) {
            setFileUploadingSnackBar({
                showSnackBar: true,
                snackBarMessage: 'The photo is uploading please wait',
                stayOpen: true,
            })
            InvestorService.uploadAvatar(investorId, adminUserSession, file).then(({data}) => {
                if (data?.photo?.url) {
                    setTimeout((event) => {
                        setFileUploadingSnackBar({showSnackBar: false})
                    }, 2000)
                    if (!userMadeChanges) {
                        setUserMadeChanges(true, null)
                    }
                } else {
                    setFileUploadingSnackBar({
                        showSnackBar: true,
                        snackBarMessage: 'This file is not supported please try another one',
                        stayOpen: true,
                    })
                }
            })
        }
    };

    const handleCloseFileSnackbar = () => {
        setFileUploadingSnackBar({showSnackBar: false})
    }

    //Improve this, move to custom hook?
    let navigations = [{name: 'Investors', onClick: () => onClose()}
        , {name: investorId ? 'Edit Investor' : 'Create Investor'}]

    const {steps, active, ...stepper} = useStepper({
        initialSteps: [
            {label: 'General', error: false},
        ]
    });

    const handleOnTabChange = (e, value) => {
        stepper.setActive(value)
    }

    const handleOnError = (step, error) => {
        stepper.setStepError(step, error);
    }

    const onOpenChanges = () => {
        InvestorService.changeLog(investorId, adminUserSession).then((response) => {
            const {changes} = response.data;
            setOpenChanges(true)
            setChanges(changes);
        });
    }

    const onOpenNotes = () => adminNotes.setOpen(true);

    const onDelete = async () => {
        try {
            if (window.confirm(`Are you sure you want to archive ${investor.name}?`)) {
                await InvestorService.delete(investor.id, adminUserSession);
                snackbar.show("Investor archived successfully", "success");
                if (userMadeChanges) {
                    setUserMadeChanges(false, () => onClose());
                } else {
                    onClose();
                }
            }
        } catch (e) {
            console.error(e);
            snackbar.show("Error when archiving investor", "warning");
        }
    }

    const handleMarkAsReviewed = useAsyncRequest(
        () => InvestorService.markAsReviewed(investor.id, adminUserSession),
        (data) => {
            setInvestor(prevState => ({...prevState, last_reviewed: data.last_reviewed}))
            snackbar.show("Investor marked as reviewed", "success")
        },
        (error) => snackbar.show(error.message, 'warning'),
    )

    const onRegenerateAiBio = () => {
        InvestorService.regenerateAiBio(investor.id, adminUserSession).then((response) => {
            const { status, message } = response.data

            if (status === 'ok') {
                alert(message)
            } else {
                snackbar.show(message, "warning");
            }
        }).catch(function (thrown) {
            console.log('Request canceled', thrown.message);
        });
    }

    const CustomActions = () => {
        return <div className="investor-form-header">
            <RegenerateBio
                show={adminUserSession.isSuperAdmin || adminUserSession.inHouseResearch}
                onRegenerate={onRegenerateAiBio}
            />
            <Button color="primary" variant="contained" onClick={onOpenNotes}>Notes</Button>
            <Button color="primary" variant="contained" onClick={onOpenChanges}>Change Log</Button>
            <Button color="primary" variant="contained" onClick={handleMarkAsReviewed}>Mark as Reviewed</Button>
        </div>
    }

    const isPmUpdated = () => {
       return !investor ? false : isDateSimilar(investor.last_pm_update, investor.latest_update, 1000)
    };

    return (
        <React.Fragment>
            <FormTabs
                navigations={navigations}
                isEditing={!!investorId}
                open={open}
                handleClose={onClose}
                onSave={handleSubmit}
                disabledSave={stepper.error() || saving}
                isLastStep={stepper.onLast()}
                setClickOnArrowBack={setClickOnArrowBack}
                userMadeChanges={userMadeChanges}
                setUserMadeChanges={setUserMadeChanges}
                fullScreen={false}
                CustomActions={CustomActions()}
                pmUpdated={isPmUpdated()}
                Stepper={
                    <StepperFormTabs steps={steps} active={active}/>
                }
                FooterActions={<FormFooter
                    isEditing={!!investorId}
                    disabledNext={stepper.currentError()}
                    disabledSave={stepper.error() || saving}
                    isLastStep={stepper.onLast()}
                    steps={steps}
                    activeStep={active}
                    onClose={onClose}
                    handleBack={stepper.handleBack}
                    handleNext={stepper.handleNext}
                    handleSubmit={handleSubmit}
                />}
                actionsLayout={'top'}
            >
                {investor &&
                    <Grid container spacing={2} className="form-content">
                        <Grid item xs={3}>
                            <InvestorDetailsSideBar
                                investor={investor}
                                handleChangeFileInput={handleChangeFileInput}
                                isDeleted={isDeleted}
                                onDelete={onDelete}
                                avatar={avatar}
                                session={adminUserSession}
                                investorId={investorId}
                                onRemoveOtherEntity={(otherEntities, crdId) => {
                                    setInvestor(prevState => ({...prevState, other_entities: otherEntities, crd_id: crdId}));
                                }}
                            />
                        </Grid>
                        <Grid item xs={9}>
                            <div className="panel-content">
                                {
                                    (steps.length >= 2) && <Paper className="panel-content-tabs" elevation={3}>
                                        <Tabs value={active}
                                              onChange={handleOnTabChange}
                                              aria-label="investor-tabs">
                                            {
                                                steps.map((s, index) => (
                                                    <Tab label={s.label} key={s.label} disabled={!investorId}/>
                                                ))
                                            }
                                        </Tabs>
                                    </Paper>
                                }

                                <TabPanel value={active} index={0}>
                                    <InvestorInfoSection
                                        isEditing={!!investorId}
                                        investor={investor}
                                        setInvestor={setInvestor}
                                        investorExtraData={investorExtraData}
                                        handleOnChange={handleOnChange}
                                        handleOnChangeInField={handleOnChangeInField}
                                        handleOnError={handleOnError}
                                        handleShowSnackBar={snackbar.show}
                                        session={adminUserSession}
                                        setUserMadeChanges={setUserMadeChanges}
                                        userMadeChanges={userMadeChanges}
                                        dataType={dataType}
                                    />
                                </TabPanel>
                            </div>
                        </Grid>
                    </Grid>
                }
            </FormTabs>

            {
                validationErrors ?
                    <CustomSnackbar
                        open={validationErrors}
                        closeHandler={handleCloseSnackBar}
                        message={validationErrors}
                        options={{
                            severity: 'warning'
                        }}
                    />
                    : null
            }
            {fileUploadingSnackBar.showSnackBar ? (
                <CustomSnackbar
                    open={fileUploadingSnackBar.showSnackBar}
                    closeHandler={handleCloseFileSnackbar}
                    message={fileUploadingSnackBar.snackBarMessage}
                    options={{
                        severity: 'warning'
                    }}
                    stayOpen={fileUploadingSnackBar.stayOpen}
                />
            ) : null}

            <NotesDialog
                label={"Add a Note"}
                body={""}
                adminNotes={adminNotes}
            />

            {
                openChanges && <ChangeLogModal
                    open={openChanges}
                    onClose={() => setOpenChanges(false)}
                    changes={changes}
                    changedBy={"Hubspot Sync"}
                    userIsPM={adminUserSession.isPM}
                />
            }
            <Prompt
                when={!clickOnArrowBack && userMadeChanges}
                message='You have unsaved changes, are you sure you want to leave?'
            />
        </React.Fragment>
    )
}

export default InvestorForm;
